import React from 'react';
import Head from 'next/head';

import { GetSeoSchemaOrg, GetSeoSchemaOrgWithJsonLd } from '../SeoSchemaOrg';
import { keywordsData } from './seo-component/SeoKeywords';
import { SeoImages } from './seo-component/SeoImages';
import { SeoUrls } from './seo-component/SeoUrls';
import { SeoTitles } from './seo-component/SeoTitles';
import { SeoDescriptions } from './seo-component/SeoDescriptions';
import { siteImage, siteUrl } from '@/constants/site';

function Seo({
    pageId,
    description,
    title,
    url,
    image,
    additionalData,
    summary = 'summary_large_image',
    author = 'Analítica Fantasy',
    publisher = 'Analítica Fantasy',
    customSeoSchema = null,
    width = 770,
    height = 440,
    keywords = null,
    canonical = null,
}) {
    url = url ?? `${siteUrl}/${SeoUrls[pageId]}`;
    title = title ?? SeoTitles[pageId];
    description = description ?? SeoDescriptions[pageId];
    keywords = keywords ?? keywordsData[pageId] ?? '';
    let route = SeoImages?.[pageId];

    if (!image) {
        if (route?.length > 0 && route.startsWith('2024')) {
            image = `${siteImage}/${SeoImages?.[pageId]}`;
        } else {
            image = `${siteImage}/banners/${
                SeoImages?.[pageId] ?? '2024/secciones/alineaciones-probables.jpg'
            }`;
        }
    }

    if (!image.includes('width=')) {
        image += `?width=770&height=440`;
    }

    // Manejar múltiples schemas si es un array
    let seoSchema = null;
    if (customSeoSchema) {
        if (Array.isArray(customSeoSchema)) {
            seoSchema = (
                <>
                    {customSeoSchema.map((schema, index) => (
                        <React.Fragment key={index}>{GetSeoSchemaOrgWithJsonLd(schema)}</React.Fragment>
                    ))}
                </>
            );
        } else {
            seoSchema = GetSeoSchemaOrgWithJsonLd(customSeoSchema);
        }
    } else {
        seoSchema = GetSeoSchemaOrg(pageId, title, url, description, image, additionalData);
    }

    // Mejorar keywords con términos adicionales si no están presentes
    const enhancedKeywords = keywords
        ? `${keywords}, fantasy fútbol, análisis fantasy, predicciones fantasy, alineaciones probables`
        : 'fantasy fútbol, análisis fantasy, predicciones fantasy, alineaciones probables, estadísticas fútbol';

    return (
        <Head>
            <title>{title}</title>
            <link rel="canonical" href={canonical ?? url} key="canonical" />

            <meta property="og:locale" content="es_ES" />
            <meta property="og:type" content="website" key="type" />
            <meta name="keywords" content={enhancedKeywords} />
            <meta property="og:site_name" content="Analitica Fantasy" />
            <meta name="description" content={description} key="name-description" />
            <meta property="og:url" content={url} key="url" />
            <meta property="og:title" content={title} key="title" />
            <meta property="og:description" content={description} key="description" />
            <meta property="og:image:secure_url" content={image} key="image" />
            <meta property="og:image" content={image} key="og-image" />
            <meta name="author" content={author} />
            <meta name="publisher" content={publisher} />
            <meta name="twitter:card" content={summary} key="twitter-card" />
            <meta
                name="twitter:image:alt"
                content={title || 'Analítica Fantasy'}
                key="twitter-card-image-alt"
            />
            <meta name="twitter:site" content="https://www.analiticafantasy.com" key="website" />
            <meta name="twitter:site:id" content="@AnaliticaLaLiga" key="website-twitter" />
            <meta name="twitter:creator" content="@AnaliticaLaLiga" key="twitter-creator" />
            <meta name="twitter:image" content={image} key="twitter-image" />
            <meta name="twitter:title" content={title} key="twitter-title" />
            <meta name="twitter:description" content={description} key="twitter-description" />

            <meta property="og:image:width" content={width} />
            <meta property="og:image:height" content={height} />

            <link rel="image_src" href={image} />

            <meta
                name="viewport"
                content="width=device-width, initial-scale=1, minimum-scale=1, maximum-scale=5, user-scalable=yes"
                key="viewport"
            />

            <meta
                name="robots"
                content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
            />

            {/* Mejoras adicionales de SEO */}
            <meta name="language" content="Spanish" />
            <meta name="geo.region" content="ES" />
            <meta name="geo.placename" content="Spain" />
            <meta name="rating" content="general" />

            {seoSchema}
        </Head>
    );
}

export default Seo;
